'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, stringToSlug } from '@/lib/utils'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useProductDetailSafe } from '../context/product-detail-context'

type Props = {
  productTitle: string
}

// Ảnh mặc định khi sản phẩm coming soon nhưng chưa có ảnh nào (CMS chưa
// upload kịp) — tránh gallery trống trơn.
const COMING_SOON_FALLBACK_IMAGE = '/image/2026/07/31/Component_3.jpg'

// Tỉ lệ khớp với width/height truyền cho next/image bên dưới — dùng để tính
// trước chiều cao ảnh sẽ render (theo chiều rộng full) mà không cần chờ ảnh
// load, từ đó biết viewport có bị dư khoảng trống trên/dưới hay không.
const IMAGE_ASPECT_RATIO = 750 / 1624

/**
 * Gallery full-bleed riêng cho layout QR mobile (?utm_source=qr&template=enhance_visual):
 * vuốt ngang để chuyển ảnh (embla carousel) — khác ProductGallery mặc định
 * (có thumbnail cột trái + lightbox); dải thumbnail preview render ở
 * InfoBarMobileQr, điều khiển qua scrollToGalleryIndexRef trong context.
 */
export const ProductGalleryQr: React.FC<Props> = ({ productTitle }) => {
  const {
    colorOptionActive,
    sortedColorOptions,
    product,
    scrollToFirstImageRef,
    setActiveGalleryIndex,
    scrollToGalleryIndexRef,
  } = useProductDetailSafe()
  const searchParams = useSearchParams()

  // colorOptionActive chỉ được set qua effect (initializeColorFromURL trong
  // useProductDetailStore), nên ở lần render đầu nó vẫn null → gallery tạm
  // fallback về product.images (ảnh mặc định, có thể không đúng màu từ URL).
  // Tự resolve màu ngay ở đây (đồng bộ, không chờ effect) để tránh flash sai
  // ảnh lúc load lần đầu — khớp với cách InfoBarMobileQr đang làm.
  const resolvedColorOption = useMemo(() => {
    if (colorOptionActive) return colorOptionActive
    const colorFromURL = searchParams.get('color')
    const matched = sortedColorOptions.find(
      (option) => stringToSlug(option.title) === colorFromURL
    )
    return matched || sortedColorOptions[0] || null
  }, [colorOptionActive, searchParams, sortedColorOptions])

  const productImage = (
    resolvedColorOption?.image?.slice(0, -1) ||
    product?.images?.slice(0, -1) ||
    []
  ).slice(-3)

  const galleryImages =
    productImage.length === 0 && product?.coming_soon
      ? [
          {
            id: 'coming-soon-fallback',
            src: COMING_SOON_FALLBACK_IMAGE,
            image: COMING_SOON_FALLBACK_IMAGE,
          },
        ]
      : productImage

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })

  // Đo container để biết ảnh (full width) render ra có thấp hơn chiều cao
  // viewport hay không — chỉ khi đó mới cần fill nền + gradient, tránh
  // gradient đè lên ảnh lúc ảnh đã lấp đầy chiều cao.
  const rootRef = useRef<HTMLDivElement>(null)
  const [hasVerticalGap, setHasVerticalGap] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const updateGap = () => {
      const { clientWidth, clientHeight } = el
      if (!clientWidth || !clientHeight) return
      const renderedImageHeight = clientWidth / IMAGE_ASPECT_RATIO
      setHasVerticalGap(renderedImageHeight < clientHeight)
    }
    updateGap()
    const resizeObserver = new ResizeObserver(updateGap)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setActiveGalleryIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi, setActiveGalleryIndex])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    onSelect()
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    scrollToFirstImageRef.current = () => emblaApi?.scrollTo(0)
    return () => {
      scrollToFirstImageRef.current = null
    }
  }, [emblaApi, scrollToFirstImageRef])

  useEffect(() => {
    scrollToGalleryIndexRef.current = (index: number) =>
      emblaApi?.scrollTo(index)
    return () => {
      scrollToGalleryIndexRef.current = null
    }
  }, [emblaApi, scrollToGalleryIndexRef])

  return (
    <div ref={rootRef} className='relative h-full w-full overflow-hidden'>
      <div className='absolute inset-x-0 top-0 z-10 flex items-start gap-2.5 bg-gradient-to-b from-neutral-100 to-transparent px-5 py-8'>
        <button
          type='button'
          className='flex-1 text-left font-criteria text-2xl font-semibold uppercase italic leading-8 text-neutral-800'
        >
          {productTitle}
        </button>
        <Link href='/' className='shrink-0 py-1.5'>
          <Image
            src='/images/logo-mobile.svg'
            alt='Logo Coolmate'
            width={32}
            height={28}
          />
        </Link>
      </div>
      <div className='h-full w-full overflow-hidden' ref={emblaRef}>
        <div className='flex h-full'>
          {galleryImages.map((item, index) => (
            <div
              key={index}
              className={cn(
                'relative flex h-full w-full flex-[0_0_100%] items-center justify-center',
                hasVerticalGap && 'bg-[#E2E2E2]'
              )}
            >
              <div className='relative w-full'>
                <Image
                  src={ImageHelper.getN7ImageUrl(item.src, 1100)}
                  alt={productTitle + ' - ' + resolvedColorOption?.title}
                  width={750}
                  height={1624}
                  className='h-auto w-full'
                  sizes='100vw'
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  unoptimized={index === 0}
                  priority={index === 0}
                />
                {hasVerticalGap && (
                  <>
                    {/* Gradient phủ mép trên/dưới của ảnh, hoà vào nền
                    neutral fill xung quanh — che vệt cắt cứng khi ảnh
                    không đủ chiều cao. */}
                    <div className='pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#E2E2E2] to-transparent' />
                    <div className='pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#E2E2E2] to-transparent' />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
